import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { transitionLabel } from '@/pages/bacen/OverviewPage';
import { Figure, PageHeader, bacenCasesHref, useCodeLabel, useFormats } from '@/pages/bacen/shared';
import { fetchBacenAppeals } from '@/services/bacen';
import type { AnswerOut, BacenGroupTable } from '@/services/types';

/**
 * What the CRSFN does to a first-instance penalty: the type transitions, the
 * fine relief on rows with a fine at both stages, and the same by office,
 * year and subject type.
 *
 * Every rate is over RULED rows and the corpus share sits beside it. A
 * recurso de oficio is the office's own referral of an acquittal or a
 * reduction, so the ex-officio mix is mostly upheld by construction; the
 * screen says what the docket contained, never how a pending appeal will go.
 */
export function BacenAppealsPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency, percent } = useFormats();
  const label = useCodeLabel();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'appeals'],
    queryFn: fetchBacenAppeals,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader page="bacen:appeals" exportPath="/api/v1/bacen/export/appeals" />

      <AsOfStrip asOf={data.as_of} />

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border bg-card p-4" data-board-outcomes>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:appeals.outcomes.title')}
          </h2>
          <Answer answer={data.board_outcomes} help={t('bacen:appeals.outcomes.help')}>
            {(o) => (
              <div className="space-y-3">
                <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                  {o.appeal_kinds.map((kind) => (
                    <div
                      key={kind.code}
                      className="rounded-md border bg-muted/30 p-2"
                      data-appeal-kind={kind.code}
                    >
                      <dt className="text-xs text-muted-foreground">
                        {label('appealKind', kind.code)}
                      </dt>
                      <dd className="font-semibold tabular-nums text-foreground">
                        <Link
                          to={bacenCasesHref({ appeal_kind: kind.code })}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {number.format(kind.count)}
                        </Link>
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          ({percent.format(kind.share)})
                        </span>
                      </dd>
                    </div>
                  ))}
                </dl>
                <ul className="space-y-1 text-sm">
                  {o.transitions.map((row) => (
                    <li
                      key={row.code}
                      className="flex items-center justify-between gap-3"
                      data-transition={row.code}
                    >
                      <span className="text-muted-foreground">
                        {transitionLabel(row.code, label)}
                      </span>
                      <span className="tabular-nums text-foreground">
                        {number.format(row.count)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(row.share)})
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                  {t('bacen:appeals.outcomes.bothDenominators', {
                    relief: number.format(o.relief),
                    reviewed: number.format(o.reviewed),
                    ofReviewed: percent.format(o.relief_share_of_reviewed),
                    total: number.format(o.total_rows),
                    ofAll: percent.format(o.relief_share_of_all),
                    pending: number.format(o.pending),
                  })}
                </p>
              </div>
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="bacen:appeals.guide.outcomes" />
          </div>
        </section>

        <section className="rounded-lg border bg-card p-4" data-review-relief>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('bacen:appeals.relief.title')}
          </h2>
          <Answer answer={data.review_relief} help={t('bacen:appeals.relief.help')}>
            {(r) => (
              <div className="space-y-3">
                <dl className="grid grid-cols-3 gap-2">
                  <Figure
                    label={t('bacen:appeals.relief.lower')}
                    value={`${number.format(r.lower)} (${percent.format(r.lower_share)})`}
                    href={bacenCasesHref({ trajectory: 'REDUCED' })}
                    testId="lower"
                  />
                  <Figure label={t('bacen:appeals.relief.equal')} value={number.format(r.equal)} />
                  <Figure
                    label={t('bacen:appeals.relief.higher')}
                    value={number.format(r.higher)}
                  />
                </dl>
                <dl className="grid grid-cols-3 gap-2">
                  <Figure
                    label={t('bacen:appeals.relief.firstTotal')}
                    value={currency.format(r.first_total)}
                  />
                  <Figure
                    label={t('bacen:appeals.relief.boardTotal')}
                    value={currency.format(r.board_total)}
                  />
                  <Figure
                    label={t('bacen:appeals.relief.medianRetained')}
                    value={
                      r.median_retained_when_lower === null
                        ? '—'
                        : percent.format(r.median_retained_when_lower)
                    }
                  />
                </dl>
                <p className="text-xs text-muted-foreground">
                  {t('bacen:appeals.relief.population', {
                    withBoth: number.format(r.with_both_fines),
                    reviewed: number.format(r.reviewed),
                  })}
                </p>
              </div>
            )}
          </Answer>
        </section>
      </div>

      <GroupTablePanel answer={data.by_office} />
      <GroupTablePanel answer={data.by_year} />
      <GroupTablePanel answer={data.by_subject} />
    </div>
  );
}

function GroupTablePanel({ answer }: { answer: AnswerOut<BacenGroupTable> }) {
  const { t } = useTranslation(['bacen']);
  const { number, percent } = useFormats();
  const label = useCodeLabel();
  const kind = answer.value?.kind ?? 'office';
  const href = (group: string) =>
    kind === 'office'
      ? bacenCasesHref({ office: group })
      : kind === 'year'
        ? bacenCasesHref({ year: Number(group) })
        : bacenCasesHref({ subject_type: group });
  return (
    <section className="rounded-lg border bg-card p-4" data-group-table={kind}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(`bacen:appeals.groups.${kind}.title`)}
      </h2>
      <Answer answer={answer} help={t(`bacen:appeals.groups.${kind}.help`)}>
        {(table) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">
                      {t(`bacen:appeals.groups.${kind}.group`)}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:appeals.groups.rows')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:appeals.groups.reviewed')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:appeals.groups.relief')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:appeals.groups.reduced')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:appeals.groups.eliminated')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('bacen:appeals.groups.typeChanged')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row) => (
                    <tr key={row.group} className="border-t" data-group={row.group}>
                      <td className="px-3 py-2 text-foreground">
                        <Link
                          to={href(row.group)}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {kind === 'subject' ? label('subjectType', row.group) : row.group}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.rows)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(row.reviewed)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(row.reviewed_share)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {number.format(row.relief)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({percent.format(row.relief_share_of_reviewed)})
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.reduced)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.eliminated)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.type_changed)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {table.suppressed > 0 ? (
              <p className="text-xs text-muted-foreground" data-suppressed>
                {t('bacen:appeals.groups.suppressed', {
                  n: number.format(table.suppressed),
                  reviewed: number.format(table.suppressed_reviewed),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}
